/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Avatar, Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirmation, useRequester } from '../../hooks';
import TutorResource, { TutorData } from '../../resources/TutorResource';
import { useEffect } from 'react';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Page } from '../../design';

export default function TutorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const deleteConfirmation = useConfirmation<TutorData>({
    getTitle: (tutor: TutorData) => `Delete tutor ${tutor.name}?`,
    getDescription: (tutor: TutorData) =>
      `You are about to delete ${tutor.name}. This action cannot be undone.`,
    onConfirm: async (tutor: TutorData) => {
      await TutorResource.deleteOne(tutor.id);
      navigate('/tutors');
    },
  });

  const fetchTutor = useRequester<TutorResource>({
    onSubmit: (id) => TutorResource.fetchOne(id),
    onError: console.log,
  });

  useEffect(() => {
    fetchTutor.submit(id);
  }, [id]);

  if (!id) return <Alert severity="error">No tutor ID provided</Alert>;
  const tutor = TutorResource.get(id).data as TutorData;
  if (!tutor) {
    return (
      <Box className="flex flex-col items-center h-full justify-center">
        {fetchTutor.error ? (
          <Alert severity="error">{fetchTutor.error}</Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full justify-center">
          <Box className="flex flex-row gap-4">
            <Avatar
              alt={tutor.name}
              src={tutor.avatar}
              sx={{
                height: 140,
                width: 140,
                outline: '2px solid',
                outlineColor: tutor.color || 'transparent',
              }}
            />
            <Box className="flex flex-col gap-1">
              <Box className="flex flex-row gap-1 items-center">
                <Typography variant="h1" sx={{ mr: 1 }}>
                  {tutor.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  (Identification: {tutor.identification})
                </Typography>
                <Box className="flex-grow"></Box>
                <IconButton color="primary" onClick={() => navigate(`/tutors/${id}/edit`)}>
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  sx={{ ml: -1 }}
                  onClick={() => deleteConfirmation.triggerFor(tutor)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Typography variant="body1" color="text.secondary" textAlign="justify">
                {tutor.intro}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box className="flex flex-col gap-8 mt-6">
          {tutor.personaTavus && (
            <Box className="flex flex-col gap-1">
              <Typography variant="h4" fontWeight="bold">
                Persona Tavus
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2, mt: 2 }}>
                {tutor.personaTavus}
              </Typography>
            </Box>
          )}
          <Box className="flex flex-col gap-1">
            <Typography variant="h4" fontWeight="bold">
              Bio
            </Typography>
            <Box className="flex flex-col gap-2 mt-2">
              <Typography variant="h6" fontWeight="bold">
                English
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2 }}>
                {tutor.bio ? tutor.bio : <i>N/A</i>}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                Japanese
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2 }}>
                {tutor.bioJp ? tutor.bioJp : <i>N/A</i>}
              </Typography>
            </Box>
          </Box>
          <Box className="flex flex-col gap-1">
            <Typography variant="h4" fontWeight="bold">
              Intro
            </Typography>
            <Box className="flex flex-col gap-2 mt-2">
              <Typography variant="h6" fontWeight="bold">
                English
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2 }}>
                {tutor.intro ? tutor.intro : <i>N/A</i>}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                Japanese
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2 }}>
                {tutor.introJp ? tutor.introJp : <i>N/A</i>}
              </Typography>
            </Box>
          </Box>
          <Box className="flex flex-col gap-1">
            <Typography variant="h4" fontWeight="bold">
              Prompts
            </Typography>

            <Box className="flex flex-col gap-4 whitespace-pre-wrap mt-2">
              {[
                { title: 'Context (Beginner)', content: tutor.contextPrompt.beginner },
                { title: 'Context (Intermediate)', content: tutor.contextPrompt.intermediate },
                { title: 'Context (Advanced)', content: tutor.contextPrompt.advanced },
                { title: 'Mission Generator', content: tutor.missionPrompt },
                { title: 'Hint Generator', content: tutor.hintPrompt },
                { title: 'General Feedback Generator', content: tutor.generalFeedbackPrompt },
                {
                  title: 'General Feedback Translator',
                  content: TutorResource.getPrompt(tutor, 'generalFeedbackTranslator'),
                },
                { title: 'Grammar Feedback Generator', content: tutor.grammarFeedbackPrompt },
                {
                  title: 'Grammar Feedback Translator',
                  content: TutorResource.getPrompt(tutor, 'grammarFeedbackTranslator'),
                },
                { title: 'Summary Generator', content: tutor.summaryPrompt },
                {
                  title: 'Summary Translator',
                  content: TutorResource.getPrompt(tutor, 'summaryTranslator'),
                },
                {
                  title: 'Word Explanation Generator',
                  content: TutorResource.getPrompt(tutor, 'wordExplanationEn'),
                },
                {
                  title: 'Word Explanation Generator (JP)',
                  content: TutorResource.getPrompt(tutor, 'wordExplanationJp'),
                },
                {
                  title: 'Word Usage Generator',
                  content: TutorResource.getPrompt(tutor, 'wordUsageEn'),
                },
                {
                  title: 'Word Usage Generator (JP)',
                  content: TutorResource.getPrompt(tutor, 'wordUsageJp'),
                },
                {
                  title: 'Word Conversation Starter',
                  content: TutorResource.getPrompt(tutor, 'wordConversation'),
                },
                ...['beginner', 'intermediate', 'advanced'].map((level) => ({
                  title: `World Travel Mode (${level})`,
                  content: TutorResource.getPrompt(tutor, 'travelMode', level as any),
                })),
              ].map(({ title, content }) => (
                <Box key={title}>
                  <Typography variant="h6" fontWeight="bold">
                    {title}
                  </Typography>
                  <Typography
                    textAlign="justify"
                    className="whitespace-pre-wrap"
                    sx={{ pl: 2, mt: 1 }}
                  >
                    {content ? content : <i>N/A</i>}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className="flex flex-col gap-1">
            <Typography variant="h4" fontWeight="bold">
              Voices
            </Typography>
            <Box className="flex flex-row gap-40">
              <Box className="flex flex-col gap-2 mt-2">
                <Typography variant="h6" fontWeight="bold">
                  English
                </Typography>
                <Box className="flex flex-col gap-1">
                  <Typography>Service: {tutor.voice.service}</Typography>
                  <Typography>Code: {tutor.voice.code}</Typography>
                  <Typography>Accent: {tutor.voice.accent}</Typography>
                  <Typography>
                    Speed: {tutor.voice.speed.beginner}/{tutor.voice.speed.intermediate}/
                    {tutor.voice.speed.advanced}
                  </Typography>
                </Box>
              </Box>
              <Box className="flex flex-col gap-1 mt-2">
                <Typography variant="h6" fontWeight="bold">
                  Japanese
                </Typography>
                <Box className="flex flex-col gap-1">
                  <Typography>Service: {tutor.voiceJp.service}</Typography>
                  <Typography>Code: {tutor.voiceJp.code}</Typography>
                  <Typography>Accent: {tutor.voiceJp.accent}</Typography>
                  <Typography>
                    Speed: {tutor.voiceJp.speed.beginner}/{tutor.voiceJp.speed.intermediate}/
                    {tutor.voiceJp.speed.advanced}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Page.Content>
    </Page>
  );
}
